/**
 * Express Interest Dialog
 * Dialog for DSA/Agents to express interest in a marketplace lead
 * Compact, mobile-responsive UI optimized for 100% zoom
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import {
    marketplaceService,
    formatLoanAmount
} from '../../../services/marketplaceService';

const ExpressInterestDialog = ({
    visible,
    onHide,
    lead,
    suggestedPercentage,
    onSuccess
}) => {
    const toast = useRef(null);

    // Form state
    const [proposedPercentage, setProposedPercentage] = useState(2.0);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Derived values
    const loanAmount = lead?.lead_snapshot?.loan_amount || lead?.loan_amount || 0;
    const ownerRequestedPct = Number(suggestedPercentage ?? lead?.commission_percentage) || 2.0;
    const estimatedPayout = (loanAmount * proposedPercentage) / 100;

    // Reset form when dialog opens
    useEffect(() => {
        if (visible) {
            const initialPercentage = Number(suggestedPercentage ?? lead?.commission_percentage ?? 2.0) || 2.0;
            setProposedPercentage(initialPercentage);
            setMessage('');
        }
    }, [visible, suggestedPercentage, lead?.commission_percentage]);

    // Submit interest
    const handleSubmit = async () => {
        if (!lead?.id) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Lead information is missing'
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await marketplaceService.expressInterestWithProposal(lead.id, {
                proposedPercentage,
                requestedRole: 'collaborator',
                contributionLevel: 'medium',
                message: message.trim() || undefined
            });

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Enquiry Submitted',
                    detail: 'Lead owner will review your proposal'
                });
                onSuccess?.(response.data);
                onHide();
            } else {
                throw new Error(response.message || 'Failed to submit interest');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to submit interest'
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Get color based on percentage value
    const getPayoutColor = (pct) => {
        if (pct <= 1.5) return '#22C55E';
        if (pct <= 3) return '#3B82F6';
        if (pct <= 5) return '#F59E0B';
        return '#EF4444';
    };

    // Format currency in Indian format
    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return '₹0';
        if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
        if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
        if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={null}
                showHeader={false}
                modal
                className="express-interest-dialog"
                contentClassName="p-0"
                closable={!submitting}
                closeOnEscape={!submitting}
            >
                {/* Header */}
                <div
                    className="express-header flex align-items-center justify-content-between"
                    style={{
                        background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
                        borderRadius: '6px 6px 0 0'
                    }}
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-send text-white header-icon" />
                        <span className="text-white font-semibold header-title">Send Enquiry</span>
                    </div>
                    <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-text p-0 close-btn"
                        style={{ color: 'rgba(255,255,255,0.9)' }}
                        onClick={onHide}
                        disabled={submitting}
                    />
                </div>

                <div className="dialog-body">
                    {/* Applicant + Loan Info */}
                    {lead && (
                        <div className="applicant-card flex align-items-center justify-content-between border-round"
                            style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}
                        >
                            <div className="flex align-items-center gap-2">
                                <div
                                    className="avatar-circle flex align-items-center justify-content-center border-circle"
                                    style={{ backgroundColor: '#EEF2FF' }}
                                >
                                    <i className="pi pi-user" style={{ color: '#4338CA' }} />
                                </div>
                                <div>
                                    <div className="font-semibold text-900 applicant-name">
                                        {lead.lead_snapshot?.borrower_name || lead.borrower_name || 'Applicant'}
                                    </div>
                                    <div className="flex align-items-center gap-1 flex-wrap">
                                        <Tag
                                            value={lead.lead_snapshot?.loan_type || lead.loan_type || 'Loan'}
                                            className="loan-tag"
                                            style={{ backgroundColor: '#EEF2FF', color: '#4338CA', border: 'none' }}
                                        />
                                        {(lead.lead_snapshot?.city || lead.city) && (
                                            <span className="text-400 location-text">
                                                • {lead.lead_snapshot?.city || lead.city}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-400 loan-label">LOAN</div>
                                <div className="font-bold text-primary loan-amount">
                                    {formatLoanAmount(loanAmount)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Owner's Expected */}
                    <div
                        className="owner-expects flex align-items-center justify-content-between border-round"
                        style={{ backgroundColor: '#FEF9C3' }}
                    >
                        <span style={{ color: '#854D0E' }} className="owner-label">
                            <i className="pi pi-info-circle mr-1" />
                            Owner's Expected
                        </span>
                        <span className="font-bold owner-pct" style={{ color: '#854D0E' }}>
                            {parseFloat(ownerRequestedPct.toFixed(3))}%
                        </span>
                    </div>

                    {/* Lead Payout Section */}
                    <div className="payout-section">
                        <div className="section-title text-800 font-semibold">Your Payout Offer</div>
                        <div className="section-subtitle text-500">% of disbursed amount to lead owner</div>

                        {/* Percentage Display */}
                        <div
                            className="payout-display text-center border-round"
                            style={{
                                backgroundColor: '#FAFAFA',
                                border: `2px solid ${getPayoutColor(proposedPercentage)}`,
                                transition: 'border-color 0.2s ease'
                            }}
                        >
                            <div className="flex align-items-center justify-content-center gap-2">
                                <div
                                    className="payout-percentage font-bold"
                                    style={{
                                        color: getPayoutColor(proposedPercentage),
                                        lineHeight: 1,
                                        transition: 'color 0.2s ease'
                                    }}
                                >
                                    {parseFloat(proposedPercentage.toFixed(3))}%
                                </div>
                                <div className="text-left">
                                    <div className="text-400 est-label">EST. PAYOUT</div>
                                    <div className="font-semibold text-900 est-amount">{formatCurrency(estimatedPayout)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Slider */}
                        <div className="slider-container">
                            <Slider
                                value={proposedPercentage}
                                onChange={(e) => setProposedPercentage(e.value)}
                                min={0.5}
                                max={10}
                                step={0.001}
                                className="w-full lead-payout-slider"
                            />
                            <div className="flex justify-content-between slider-labels text-400">
                                <span>0.5%</span>
                                <span>10%</span>
                            </div>
                        </div>

                        {/* Direct Input */}
                        <div className="flex align-items-center justify-content-center gap-2 direct-input">
                            <span className="text-500">Or enter:</span>
                            <InputNumber
                                value={proposedPercentage}
                                onValueChange={(e) => setProposedPercentage(Math.min(10, Math.max(0.5, e.value || 0.5)))}
                                mode="decimal"
                                minFractionDigits={1}
                                maxFractionDigits={3}
                                min={0.5}
                                max={10}
                                suffix="%"
                                inputClassName="text-center font-bold payout-input"
                                inputStyle={{
                                    color: getPayoutColor(proposedPercentage),
                                    borderRadius: '6px'
                                }}
                                showButtons={false}
                            />
                        </div>
                    </div>

                    {/* Info Note */}
                    <div
                        className="info-note flex align-items-center gap-2 border-round"
                        style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }}
                    >
                        <i className="pi pi-check-circle flex-shrink-0" style={{ color: '#16A34A' }} />
                        <span style={{ color: '#166534' }}>
                            Auto-debited from wallet on disbursement
                        </span>
                    </div>

                    {/* Message */}
                    <div className="message-section">
                        <div className="flex align-items-center justify-content-between mb-1">
                            <span className="text-800 font-semibold message-label">Message</span>
                            <span className="text-400 optional-label">Optional</span>
                        </div>
                        <InputTextarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full message-input"
                            placeholder="Introduce yourself or mention expertise..."
                            maxLength={500}
                            rows={2}
                            autoResize
                            style={{ resize: 'none', borderRadius: '6px' }}
                        />
                        <div className="text-right text-400 char-count">{message.length}/500</div>
                    </div>
                </div>

                {/* Footer */}
                <div
                    className="dialog-footer flex gap-2 justify-content-end"
                    style={{ backgroundColor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}
                >
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        className="p-button-text p-button-secondary cancel-btn"
                        onClick={onHide}
                        disabled={submitting}
                    />
                    <Button
                        label="Submit"
                        icon="pi pi-check"
                        className="submit-btn"
                        onClick={handleSubmit}
                        loading={submitting}
                        style={{
                            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                            border: 'none',
                            fontWeight: '600'
                        }}
                    />
                </div>

                {/* Compact Responsive Styles - Optimized for 100% zoom */}
                <style>{`
                    /* Base - Desktop (Compact for 100% zoom) */
                    .express-interest-dialog {
                        width: 380px !important;
                        max-width: 95vw !important;
                    }
                    .express-interest-dialog .p-dialog-content {
                        padding: 0 !important;
                        border-radius: 6px !important;
                        overflow: hidden;
                    }
                    .express-header {
                        padding: 0.625rem 0.875rem;
                    }
                    .header-icon {
                        font-size: 0.875rem;
                    }
                    .header-title {
                        font-size: 0.875rem;
                    }
                    .close-btn {
                        width: 1.5rem;
                        height: 1.5rem;
                    }
                    .close-btn .pi {
                        font-size: 0.75rem;
                    }
                    .dialog-body {
                        padding: 0.75rem;
                    }
                    .applicant-card {
                        padding: 0.5rem;
                        margin-bottom: 0.5rem;
                    }
                    .avatar-circle {
                        width: 1.75rem;
                        height: 1.75rem;
                    }
                    .avatar-circle i {
                        font-size: 0.75rem;
                    }
                    .applicant-name {
                        font-size: 0.8125rem;
                        margin-bottom: 0.125rem;
                    }
                    .loan-tag {
                        font-size: 0.625rem;
                        padding: 0.125rem 0.375rem;
                    }
                    .location-text {
                        font-size: 0.625rem;
                    }
                    .loan-label {
                        font-size: 0.5625rem;
                        margin-bottom: 0.0625rem;
                    }
                    .loan-amount {
                        font-size: 0.875rem;
                    }
                    .owner-expects {
                        padding: 0.375rem 0.625rem;
                        margin-bottom: 0.625rem;
                    }
                    .owner-label {
                        font-size: 0.6875rem;
                    }
                    .owner-label i {
                        font-size: 0.625rem;
                    }
                    .owner-pct {
                        font-size: 0.8125rem;
                    }
                    .payout-section {
                        margin-bottom: 0.5rem;
                    }
                    .section-title {
                        font-size: 0.8125rem;
                        margin-bottom: 0.125rem;
                    }
                    .section-subtitle {
                        font-size: 0.6875rem;
                        margin-bottom: 0.5rem;
                    }
                    .payout-display {
                        padding: 0.625rem;
                        margin-bottom: 0.5rem;
                    }
                    .payout-percentage {
                        font-size: 1.75rem;
                    }
                    .est-label {
                        font-size: 0.5625rem;
                        margin-bottom: 0.0625rem;
                    }
                    .est-amount {
                        font-size: 0.8125rem;
                    }
                    .slider-container {
                        padding: 0 0.25rem;
                        margin-bottom: 0.375rem;
                    }
                    .slider-labels {
                        font-size: 0.625rem;
                        margin-top: 0.25rem;
                    }
                    .direct-input {
                        margin-bottom: 0;
                    }
                    .direct-input span {
                        font-size: 0.6875rem;
                    }
                    .payout-input {
                        font-size: 0.875rem;
                        width: 4rem;
                        padding: 0.25rem 0.375rem;
                    }
                    .info-note {
                        padding: 0.375rem 0.5rem;
                        margin-top: 0.5rem;
                        margin-bottom: 0.5rem;
                    }
                    .info-note i {
                        font-size: 0.6875rem;
                    }
                    .info-note span {
                        font-size: 0.6875rem;
                        line-height: 1.3;
                    }
                    .message-section {
                        margin-bottom: 0;
                    }
                    .message-label {
                        font-size: 0.75rem;
                    }
                    .optional-label {
                        font-size: 0.625rem;
                    }
                    .message-input {
                        font-size: 0.75rem;
                        padding: 0.5rem;
                    }
                    .message-input textarea {
                        min-height: 48px;
                    }
                    .char-count {
                        font-size: 0.5625rem;
                        margin-top: 0.125rem;
                    }
                    .dialog-footer {
                        padding: 0.5rem 0.75rem;
                    }
                    .cancel-btn {
                        font-size: 0.75rem;
                        padding: 0.375rem 0.625rem;
                    }
                    .submit-btn {
                        font-size: 0.75rem;
                        padding: 0.375rem 0.75rem;
                    }

                    /* Slider Styles - Compact */
                    .lead-payout-slider.p-slider {
                        background: linear-gradient(to right, #22C55E 0%, #3B82F6 35%, #F59E0B 65%, #EF4444 100%) !important;
                        height: 6px !important;
                        border-radius: 3px !important;
                    }
                    .lead-payout-slider .p-slider-range {
                        background: transparent !important;
                    }
                    .lead-payout-slider .p-slider-handle {
                        width: 18px !important;
                        height: 18px !important;
                        margin-top: -6px !important;
                        background: white !important;
                        border: 2px solid #3B82F6 !important;
                        box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
                        transition: transform 0.15s ease !important;
                    }
                    .lead-payout-slider .p-slider-handle:hover {
                        transform: scale(1.1) !important;
                    }

                    /* Mobile - Narrower width, better proportions */
                    @media (max-width: 480px) {
                        .express-interest-dialog {
                            width: 320px !important;
                            max-width: 90vw !important;
                            margin: 0.5rem auto !important;
                        }
                        .express-header {
                            padding: 0.75rem 1rem;
                        }
                        .header-icon {
                            font-size: 0.875rem;
                        }
                        .header-title {
                            font-size: 0.875rem;
                        }
                        .close-btn {
                            width: 1.5rem;
                            height: 1.5rem;
                        }
                        .dialog-body {
                            padding: 0.875rem;
                        }
                        .applicant-card {
                            padding: 0.625rem;
                            margin-bottom: 0.625rem;
                        }
                        .avatar-circle {
                            width: 2rem;
                            height: 2rem;
                        }
                        .avatar-circle i {
                            font-size: 0.8125rem;
                        }
                        .applicant-name {
                            font-size: 0.8125rem;
                        }
                        .loan-tag {
                            font-size: 0.625rem;
                            padding: 0.125rem 0.375rem;
                        }
                        .location-text {
                            font-size: 0.625rem;
                        }
                        .loan-label {
                            font-size: 0.5625rem;
                        }
                        .loan-amount {
                            font-size: 0.9375rem;
                        }
                        .owner-expects {
                            padding: 0.5rem 0.75rem;
                            margin-bottom: 0.75rem;
                        }
                        .owner-label {
                            font-size: 0.75rem;
                        }
                        .owner-label i {
                            font-size: 0.6875rem;
                        }
                        .owner-pct {
                            font-size: 0.875rem;
                        }
                        .payout-section {
                            margin-bottom: 0.625rem;
                        }
                        .section-title {
                            font-size: 0.8125rem;
                            margin-bottom: 0.25rem;
                        }
                        .section-subtitle {
                            font-size: 0.6875rem;
                            margin-bottom: 0.625rem;
                        }
                        .payout-display {
                            padding: 0.75rem;
                            margin-bottom: 0.625rem;
                        }
                        .payout-percentage {
                            font-size: 1.875rem;
                        }
                        .est-label {
                            font-size: 0.5625rem;
                        }
                        .est-amount {
                            font-size: 0.875rem;
                        }
                        .slider-container {
                            padding: 0 0.375rem;
                            margin-bottom: 0.5rem;
                        }
                        .slider-labels {
                            font-size: 0.6875rem;
                            margin-top: 0.375rem;
                        }
                        .direct-input {
                            margin-top: 0.25rem;
                        }
                        .direct-input span {
                            font-size: 0.75rem;
                        }
                        .payout-input {
                            font-size: 0.9375rem;
                            width: 4.5rem;
                            padding: 0.3125rem 0.5rem;
                        }
                        .info-note {
                            padding: 0.5rem 0.625rem;
                            margin-top: 0.625rem;
                            margin-bottom: 0.625rem;
                        }
                        .info-note i {
                            font-size: 0.75rem;
                        }
                        .info-note span {
                            font-size: 0.6875rem;
                            line-height: 1.4;
                        }
                        .message-label {
                            font-size: 0.75rem;
                        }
                        .optional-label {
                            font-size: 0.625rem;
                        }
                        .message-input {
                            font-size: 0.75rem;
                            padding: 0.5rem;
                        }
                        .message-input textarea {
                            min-height: 52px;
                        }
                        .char-count {
                            font-size: 0.5625rem;
                            margin-top: 0.1875rem;
                        }
                        .dialog-footer {
                            padding: 0.625rem 0.875rem;
                        }
                        .cancel-btn {
                            font-size: 0.75rem;
                            padding: 0.4375rem 0.75rem;
                        }
                        .submit-btn {
                            font-size: 0.75rem;
                            padding: 0.4375rem 0.875rem;
                        }
                        .lead-payout-slider.p-slider {
                            height: 6px !important;
                        }
                        .lead-payout-slider .p-slider-handle {
                            width: 18px !important;
                            height: 18px !important;
                            margin-top: -6px !important;
                        }
                    }
                `}</style>
            </Dialog>
        </>
    );
};

export default ExpressInterestDialog;
